import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import FeatureRouter from './features/FeatureRouter';
import { 
  MapPin, 
  Target, 
  Users, 
  Presentation, 
  Clock, 
  Eye, 
  Award, 
  Bot, 
  Crown, 
  Calendar,
  Building,
  Newspaper,
  PlusCircle,
  BookOpen,
  UserCheck,
  Briefcase,
  FileText,
  MessageCircle,
  BarChart3,
  TestTube,
  Play,
  Lock,
  Shield,
  Sparkles,
  Zap,
  Rocket,
  Star,
  Globe,
  Heart,
  TrendingUp,
  Lightbulb,
  Compass,
  Gift,
  Trophy,
  Diamond
} from 'lucide-react';

const PremiumFeatures = () => {
  const { isPremiumUser, premiumFeatures } = usePremiumAccess();
  const { isAdmin } = useAdminStatus();
  const [activeFeature, setActiveFeature] = useState<string | null>(null);

  // Admin users get all features enabled
  const hasAccess = isPremiumUser || isAdmin;

  console.log('PremiumFeatures - Access state:', { 
    isPremiumUser, 
    isAdmin, 
    hasAccess,
    premiumFeatures 
  });

  if (activeFeature) {
    return (
      <FeatureRouter 
        activeFeature={activeFeature} 
        onBack={() => setActiveFeature(null)} 
      />
    );
  }

  const teenFeatures = [
    {
      icon: Compass,
      title: 'Career Map 360',
      description: 'Complete career planning and roadmap tool with AI guidance',
      enabled: hasAccess,
      featureKey: 'careerMap360',
      interactive: true,
      popular: true,
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50'
    },
    {
      icon: Sparkles,
      title: 'DreamBoard+ Vision Planning',
      description: 'Advanced vision and goal planning workspace with progress tracking',
      enabled: hasAccess,
      featureKey: 'dreamBoardPlus',
      interactive: true,
      popular: true,
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50'
    },
    {
      icon: Users,
      title: 'Teen-Only Fellowship Programs',
      description: 'Exclusive access to fellowship opportunities for teens',
      enabled: hasAccess,
      featureKey: 'teenFellowshipPrograms',
      interactive: true,
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50'
    },
    {
      icon: Rocket,
      title: 'Pitch Playground PRO',
      description: 'Advanced pitch development and feedback tools',
      enabled: hasAccess,
      featureKey: 'pitchPlaygroundPro',
      interactive: true,
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50'
    },
    {
      icon: Clock,
      title: '1:1 Monthly Mentor Power Hours',
      description: 'Dedicated monthly mentorship sessions with industry experts',
      enabled: hasAccess,
      featureKey: 'monthlyMentorHours',
      interactive: true,
      gradient: 'from-indigo-500 to-blue-500',
      bgGradient: 'from-indigo-50 to-blue-50'
    },
    {
      icon: Eye,
      title: 'Shadow a Founder Experience',
      description: 'Exclusive virtual shadowing opportunities with successful founders',
      enabled: hasAccess,
      featureKey: 'shadowFounderExperience',
      interactive: true,
      gradient: 'from-yellow-500 to-orange-500',
      bgGradient: 'from-yellow-50 to-orange-50'
    },
    {
      icon: Trophy,
      title: 'Verified Skill Portfolio Builder',
      description: 'Professional skill verification and portfolio building tools',
      enabled: hasAccess,
      featureKey: 'verifiedSkillPortfolio',
      interactive: true,
      gradient: 'from-green-500 to-teal-500',
      bgGradient: 'from-green-50 to-teal-50'
    },
    {
      icon: Bot,
      title: 'AI-Powered Career Coach',
      description: 'Personalized AI guidance for career development and planning',
      enabled: hasAccess,
      featureKey: 'aiCareerCoach',
      interactive: true,
      popular: true,
      gradient: 'from-violet-500 to-purple-500',
      bgGradient: 'from-violet-50 to-purple-50'
    },
    {
      icon: Crown,
      title: 'Community Leader Badge',
      description: 'Special recognition and leadership status in the community',
      enabled: hasAccess,
      featureKey: 'communityLeaderBadge',
      interactive: false,
      gradient: 'from-amber-500 to-yellow-500',
      bgGradient: 'from-amber-50 to-yellow-50'
    },
    {
      icon: Zap,
      title: 'Early Access to Events',
      description: 'Priority access to internships, contests, and exclusive events',
      enabled: hasAccess,
      featureKey: 'earlyAccessEvents',
      interactive: false,
      gradient: 'from-pink-500 to-rose-500',
      bgGradient: 'from-pink-50 to-rose-50'
    }
  ];

  const startupFeatures = [
    {
      icon: Building,
      title: 'Host Micro-Internship Program',
      description: 'Create and manage micro-internship opportunities for teens',
      enabled: hasAccess,
      featureKey: 'microInternshipProgram',
      interactive: false,
      popular: true,
      gradient: 'from-slate-500 to-gray-600',
      bgGradient: 'from-slate-50 to-gray-50'
    },
    {
      icon: Newspaper,
      title: 'Startup Feature on Newsroom',
      description: 'Get featured in TeenVisor newsroom and media coverage',
      enabled: hasAccess,
      featureKey: 'startupNewsroomFeature',
      interactive: false,
      gradient: 'from-blue-600 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50'
    },
    {
      icon: Heart,
      title: 'Teen Community Building Suite',
      description: 'Advanced tools for building and managing teen communities',
      enabled: hasAccess,
      featureKey: 'teenCommunityBuilding',
      interactive: false,
      gradient: 'from-red-500 to-pink-500',
      bgGradient: 'from-red-50 to-pink-50'
    },
    {
      icon: BookOpen,
      title: 'Course & Challenge Creator',
      description: 'Create educational content and challenges for teens',
      enabled: hasAccess,
      featureKey: 'courseCreatorAccess',
      interactive: false,
      gradient: 'from-emerald-500 to-green-500',
      bgGradient: 'from-emerald-50 to-green-50'
    },
    {
      icon: Diamond,
      title: 'Verified Teen Talent Match',
      description: 'Connect with verified teen talent for your projects',
      enabled: hasAccess,
      featureKey: 'talentMatchVerified',
      interactive: false,
      popular: true,
      gradient: 'from-cyan-500 to-blue-500',
      bgGradient: 'from-cyan-50 to-blue-50'
    },
    {
      icon: Gift,
      title: 'Premium Opportunity Posting',
      description: 'Share exclusive opportunities and high-value projects',
      enabled: hasAccess,
      featureKey: 'premiumOpportunityPosting',
      interactive: false,
      gradient: 'from-purple-600 to-indigo-600',
      bgGradient: 'from-purple-50 to-indigo-50'
    },
    {
      icon: Lightbulb,
      title: 'Startup Stories Publishing',
      description: 'Publish your startup story in our Founders Series',
      enabled: hasAccess,
      featureKey: 'startupStoriesPublishing',
      interactive: false,
      gradient: 'from-yellow-500 to-amber-500',
      bgGradient: 'from-yellow-50 to-amber-50'
    },
    {
      icon: MessageCircle,
      title: 'Invite-Only Roundtables',
      description: 'Exclusive discussions with teen innovators and founders',
      enabled: hasAccess,
      featureKey: 'inviteOnlyRoundtables',
      interactive: false,
      gradient: 'from-teal-500 to-cyan-500',
      bgGradient: 'from-teal-50 to-cyan-50'
    },
    {
      icon: TrendingUp,
      title: 'Analytics Dashboard',
      description: 'Track and analyze community engagement metrics',
      enabled: hasAccess,
      featureKey: 'analyticsEngagement',
      interactive: false,
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50'
    },
    {
      icon: Globe,
      title: 'Teen Market Testing Tools',
      description: 'Test products and ideas with targeted teen demographics',
      enabled: hasAccess,
      featureKey: 'teenMarketTesting',
      interactive: false,
      gradient: 'from-indigo-500 to-purple-500',
      bgGradient: 'from-indigo-50 to-purple-50'
    }
  ];

  const renderFeatureCard = (feature: any, index: number) => (
    <Card 
      key={index} 
      className={`group relative transition-all duration-500 hover:shadow-2xl hover:scale-105 overflow-hidden bg-gradient-to-br ${feature.bgGradient} hover:shadow-xl border-transparent`}
    >
      {/* Animated background gradient - always visible */}
      <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
      
      {/* Popular badge with animation - always visible */}
      {feature.popular && (
        <div className="absolute -top-2 -right-2 z-10">
          <Badge className={`bg-gradient-to-r ${feature.gradient} text-white animate-pulse shadow-lg`}>
            <Star className="h-3 w-3 mr-1" />
            Popular
          </Badge>
        </div>
      )}
      
      <CardHeader className="pb-3 relative z-10">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className={`relative p-3 rounded-xl transition-all duration-300 group-hover:scale-110 bg-gradient-to-r ${feature.gradient} shadow-lg`}>
            {/* Animated icon container - always animated */}
            <div className="relative">
              <feature.icon className="h-6 w-6 text-white group-hover:rotate-12 transition-all duration-300" />
              
              {/* Sparkle animation - always visible */}
              <div className="absolute -top-1 -right-1">
                <Sparkles className="h-3 w-3 text-yellow-300 animate-pulse" />
              </div>
            </div>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`transition-all duration-300 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent font-semibold`}>
                {feature.title}
              </span>
              {feature.enabled ? (
                <Badge className={`bg-gradient-to-r ${feature.gradient} text-white text-xs animate-bounce`}>
                  {isAdmin ? '🛡️ Admin' : '✨ Active'}
                </Badge>
              ) : (
                <div className="flex items-center gap-1">
                  <Lock className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-400">Premium</span>
                </div>
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pt-0 relative z-10">
        <CardDescription className="text-sm mb-4 leading-relaxed group-hover:text-gray-700 transition-colors">
          {feature.description}
        </CardDescription>
        
        <div className="space-y-2">
          {feature.enabled && (
            <Button 
              size="sm" 
              className={`w-full bg-gradient-to-r ${feature.gradient} hover:shadow-lg transform transition-all duration-300 hover:scale-105 text-white group-hover:animate-pulse`}
              onClick={() => setActiveFeature(feature.featureKey)}
            >
              <Play className="h-4 w-4 mr-2 group-hover:animate-bounce" />
              {feature.interactive ? 'Launch Feature' : 'View Feature'}
            </Button>
          )}
          
          {!feature.enabled && (
            <Button 
              size="sm" 
              variant="outline"
              className={`w-full border-2 transition-all duration-300 hover:scale-105 group-hover:shadow-md bg-gradient-to-r ${feature.gradient} bg-opacity-10 border-gray-300 text-gray-500 hover:border-[#FF8C00] hover:text-[#FF8C00] hover:bg-orange-50`}
              onClick={() => window.open('/pricing', '_blank')}
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to Unlock
            </Button>
          )}
        </div>
      </CardContent>
      
      {/* Hover effect overlay - always visible */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-5`}></div>
        <div className="absolute top-4 right-4">
          <Zap className="h-5 w-5 text-yellow-400 animate-pulse" />
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Animated header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles className="h-8 w-8 text-[#FF8C00] animate-pulse" />
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#FF8C00] to-orange-600 bg-clip-text text-transparent">
            Premium Features
          </h2>
          <Sparkles className="h-8 w-8 text-[#FF8C00] animate-pulse" />
        </div>
        <p className="text-muted-foreground">Unlock powerful tools to accelerate your journey</p>
      </div>

      {/* Teen Features */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <Users className="h-6 w-6 text-[#FF8C00]" />
            <div className="absolute -top-1 -right-1">
              <Star className="h-3 w-3 text-yellow-400 animate-spin" />
            </div>
          </div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            For Teens / Students
          </h3>
          <Badge variant="outline" className="border-[#FF8C00] text-[#FF8C00] animate-pulse">
            <Crown className="h-3 w-3 mr-1" />
            TeenVisor Premium
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teenFeatures.map(renderFeatureCard)}
        </div>
      </div>

      {/* Startup Features */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <Building className="h-6 w-6 text-[#FF8C00]" />
            <div className="absolute -top-1 -right-1">
              <Rocket className="h-3 w-3 text-blue-400 animate-bounce" />
            </div>
          </div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            For Startups / Organizations
          </h3>
          <Badge variant="outline" className="border-[#FF8C00] text-[#FF8C00] animate-pulse">
            <Briefcase className="h-3 w-3 mr-1" />
            TeenVisor for Startups
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {startupFeatures.map(renderFeatureCard)}
        </div>
      </div>

      {/* Feature Statistics with animations */}
      <div className="bg-gradient-to-r from-orange-50 via-yellow-50 to-orange-50 p-8 rounded-2xl shadow-lg border border-orange-200">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Trophy className="h-6 w-6 text-[#FF8C00] animate-bounce" />
          <h4 className="text-xl font-bold bg-gradient-to-r from-[#FF8C00] to-orange-600 bg-clip-text text-transparent">
            {hasAccess ? `Your ${isAdmin ? 'Admin' : 'Premium'} Access` : 'Premium Features Overview'}
          </h4>
          <Trophy className="h-6 w-6 text-[#FF8C00] animate-bounce" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="group cursor-pointer transition-transform hover:scale-105">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 group-hover:animate-pulse">
              <span className="text-2xl font-bold text-white">{teenFeatures.length}</span>
            </div>
            <p className="text-sm text-muted-foreground font-medium">Teen Features Available</p>
          </div>
          <div className="group cursor-pointer transition-transform hover:scale-105">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 group-hover:animate-pulse">
              <span className="text-2xl font-bold text-white">{startupFeatures.length}</span>
            </div>
            <p className="text-sm text-muted-foreground font-medium">Startup Features Available</p>
          </div>
          <div className="group cursor-pointer transition-transform hover:scale-105">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 group-hover:animate-pulse">
              <span className="text-2xl font-bold text-white">
                {[...teenFeatures, ...startupFeatures].filter(f => f.interactive).length}
              </span>
            </div>
            <p className="text-sm text-muted-foreground font-medium">Interactive Tools</p>
          </div>
          <div className="group cursor-pointer transition-transform hover:scale-105">
            <div className="bg-gradient-to-r from-[#FF8C00] to-orange-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 group-hover:animate-spin">
              <span className="text-2xl">{hasAccess ? (isAdmin ? '🛡️' : '✨') : '🔒'}</span>
            </div>
            <p className="text-sm text-muted-foreground font-medium">
              {hasAccess ? (isAdmin ? 'Admin' : 'Premium') : 'Free'} Access
            </p>
          </div>
        </div>
        
        {!hasAccess && (
          <div className="mt-6 text-center">
            <Button 
              onClick={() => window.open('/pricing', '_blank')}
              className="bg-gradient-to-r from-[#FF8C00] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 text-lg font-semibold animate-pulse"
            >
              <Crown className="h-5 w-5 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumFeatures;
